/**
 * Serializes MSAL interactive token requests to prevent
 * "interaction_in_progress" errors when multiple components
 * simultaneously need token renewal.
 *
 * MSAL only allows one popup/redirect at a time. When a token expires
 * after a long session, many components fire acquireTokenSilent concurrently,
 * all fail, and all fall back to interaction — causing the error.
 *
 * This module ensures only one interaction runs; other callers wait for it,
 * then retry silent acquisition (the completed interaction refreshes the cache).
 *
 * Interactive fallback comes in two flavors, chosen per call via the
 * `fallback` option:
 *   - 'popup'    (default): opens a popup. Good for optional/incremental
 *                 consent (e.g. Graph scopes) where losing page state is bad
 *                 and the caller treats failure as non-critical.
 *   - 'redirect': full-page redirect. Used for the critical app-auth token,
 *                 because redirect survives popup blockers AND third-party
 *                 cookie blocking (guest/B2B accounts, Safari ITP) — the common
 *                 causes of a failed silent-iframe renewal. Under MSAL v5 that
 *                 failure surfaces as `timed_out` (v4 called it
 *                 `monitor_window_timeout`, a code v5 no longer throws).
 *
 * Both flavors contend for the SAME lock inside MSAL, so the two guards below
 * cross-check each other: a Graph popup left open would otherwise make the
 * app-auth redirect throw `interaction_in_progress`, and vice versa.
 */

import { clearStaleInteractionLock } from './msalInteractionLock';
import { Sentry } from '../analytics/sentry';

/**
 * Record why silent acquisition failed and which fallback we took.
 *
 * Deliberately a breadcrumb, not captureException. Silent failure is a normal,
 * recoverable event on this path — capturing it would add an event per token
 * renewal for no benefit. But it was previously swallowed by a bare `catch {}`
 * with no trace at all, which meant we had *zero* telemetry on silent-renewal
 * health: the iframe timing out for every user in production would have looked
 * identical to it never happening. That blind spot is why the MSAL v5 migration
 * could not establish a before/after baseline (see
 * plans/msal-v5-upgrade-2026-08-25.md).
 *
 * As a breadcrumb it costs nothing and ships attached to whatever error does
 * eventually surface — so the next "Unable to authenticate" or
 * interaction_in_progress report arrives already carrying the sequence of
 * silent failures that led to it, with error codes.
 */
function noteSilentFailure(err, fallback, scopes) {
  try {
    Sentry.addBreadcrumb({
      category: 'auth',
      level: 'warning',
      message: 'acquireTokenSilent failed, falling back to interaction',
      data: {
        // v5 codes worth recognising here: `timed_out` (the iframe bridge did
        // not respond, replacing v4's monitor_window_timeout),
        // `redirect_bridge_empty_response`, and the interaction_required family.
        errorCode: err?.errorCode || 'unknown',
        fallback,
        scopes: Array.isArray(scopes) ? scopes.join(' ') : String(scopes),
      },
    });
  } catch {
    // Telemetry must never break authentication.
  }
}

let activePopup = null;

// Guards the full-page redirect fallback so concurrent callers don't each fire
// acquireTokenRedirect (which would throw interaction_in_progress). The first
// caller navigates away; the rest park on a never-resolving promise while the
// browser unloads.
let redirectInFlight = false;

// sessionStorage key used to break redirect loops: if we come back from a
// redirect and silent acquisition STILL fails, we surface an error instead of
// bouncing the user to Microsoft again indefinitely.
const REDIRECT_ATTEMPT_KEY = 'msalRedirectAttempt';
// Reuses the same key App.jsx / authConfig.js consume to restore the user's
// location after handleRedirectPromise() resolves the redirect on return.
const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';

// Deduplicates concurrent silent acquisitions. When a page fires several API
// calls at once, each calls acquireTokenSilent for the same scopes; sharing the
// in-flight promise avoids redundant work. Keyed by account + scopes.
const silentInflight = new Map();

function silentKey(account, scopes) {
  return `${account?.homeAccountId || ''}|${scopes.join(' ')}`;
}

/**
 * Test-only: reset module-level interaction guards between test cases.
 * Not used in application code (the redirect guard is meant to persist across
 * a real page navigation).
 */
export function __resetInteractionStateForTests() {
  activePopup = null;
  redirectInFlight = false;
  silentInflight.clear();
}

// Small helpers so a missing/blocked sessionStorage never breaks auth.
function safeSessionGet(key) {
  try { return sessionStorage.getItem(key); } catch { return null; }
}
function safeSessionSet(key, value) {
  try { sessionStorage.setItem(key, value); } catch { /* storage unavailable */ }
}
function safeSessionRemove(key) {
  try { sessionStorage.removeItem(key); } catch { /* storage unavailable */ }
}

// The `errorCode` check is the load-bearing one. The message check is a
// belt-and-braces fallback for a non-MSAL error that merely mentions the string:
// under v5 an MSAL message is only ever
// `See https://aka.ms/msal.js.errors#interaction_in_progress for details`, so it
// happens to still match via the URL fragment — but do not rely on that.
function isInteractionInProgressError(err) {
  return err?.errorCode === 'interaction_in_progress'
    || (typeof err?.message === 'string' && err.message.includes('interaction_in_progress'));
}

/**
 * Runs an interactive MSAL call, recovering from an orphaned interaction lock.
 *
 * The guards above mean we only reach an interactive call when this module
 * believes no interaction is running — so if MSAL still reports one, its flag is
 * stale (a legacy localStorage copy, or a popup whose promise never settled).
 * MSAL cannot clear that itself, and the user would be stuck until they wiped
 * site data. Clear it and retry exactly once; a second failure is real and
 * surfaces to the caller.
 */
async function runInteractive(fn) {
  try {
    return await fn();
  } catch (err) {
    if (!isInteractionInProgressError(err)) throw err;
    clearStaleInteractionLock();
    return fn();
  }
}

/**
 * Acquire a token, deduplicating concurrent interactive requests.
 *
 * @param {import("@azure/msal-browser").PublicClientApplication} instance
 * @param {import("@azure/msal-browser").AccountInfo} account
 * @param {string[]} scopes
 * @param {object} [options]
 * @param {boolean} [options.forceRefresh] - Force a fresh token from the server
 * @param {'idToken'|'accessToken'} [options.returnField] - Which token to return
 * @param {'popup'|'redirect'} [options.fallback] - Interactive fallback style
 * @returns {Promise<string>} The requested token
 */
export async function acquireToken(
  instance,
  account,
  scopes,
  { forceRefresh = false, returnField = 'idToken', fallback = 'popup' } = {}
) {
  // 1. Try silent acquisition (cached / refresh token).
  //    Concurrent callers with the same account+scopes share one in-flight
  //    request. forceRefresh bypasses the cache so the 401-retry path always
  //    gets a fresh token rather than a stale in-flight one.
  try {
    let silent;
    if (forceRefresh) {
      silent = instance.acquireTokenSilent({ scopes, account, forceRefresh: true });
    } else {
      const key = silentKey(account, scopes);
      silent = silentInflight.get(key);
      if (!silent) {
        silent = instance
          .acquireTokenSilent({ scopes, account, forceRefresh: false })
          .finally(() => silentInflight.delete(key));
        silentInflight.set(key, silent);
      }
    }
    const response = await silent;
    // Silent worked — clear any prior redirect-loop guard for this session.
    safeSessionRemove(REDIRECT_ATTEMPT_KEY);
    return response[returnField];
  } catch (err) {
    // Silent failed — need interactive. Leave a breadcrumb rather than
    // swallowing it entirely; see noteSilentFailure above.
    noteSilentFailure(err, fallback, scopes);
  }

  // 2. Interactive fallback.
  if (fallback === 'redirect') {
    return acquireViaRedirect(instance, account, scopes, returnField);
  }
  return acquireViaPopup(instance, account, scopes, returnField);
}

/**
 * Full-page redirect fallback. Navigates away to Microsoft, so this promise
 * normally never resolves — the page unloads. On return, authConfig's
 * handleRedirectPromise() caches the fresh token and restores the saved
 * location, and silent acquisition succeeds on the reloaded page.
 *
 * MSAL v5 added a `redirectNavigationTimeout` (30s default): acquireTokenRedirect
 * now rejects if the navigation never happens, where v4 simply hung forever.
 * That makes the catch below a live path for the first time — a wedged redirect
 * surfaces to the caller instead of parking on `new Promise(() => {})`.
 */
async function acquireViaRedirect(instance, account, scopes, returnField = 'idToken') {
  // Loop guard: if we already did a full redirect this session and silent
  // STILL fails, don't bounce the user again — surface a clean error.
  if (safeSessionGet(REDIRECT_ATTEMPT_KEY) === '1') {
    throw new Error('Unable to authenticate. Please sign out and sign in again.');
  }

  // A popup opened by another caller (Graph consent) holds MSAL's single
  // interaction lock. Wait it out and retry silent before navigating the whole
  // page away — the completed popup usually refreshes the cache for us.
  if (activePopup) {
    try { await activePopup; } catch { /* dismissed popup — try anyway */ }
    try {
      const response = await instance.acquireTokenSilent({ scopes, account, forceRefresh: true });
      return response[returnField];
    } catch {
      // Still need the redirect.
    }
  }

  // Only the first caller navigates; the rest park until the page unloads so
  // no downstream code runs with an undefined token.
  if (!redirectInFlight) {
    redirectInFlight = true;
    safeSessionSet(REDIRECT_ATTEMPT_KEY, '1');
    // Save where the user is so PostLoginRedirect can return them after the
    // round-trip (same mechanism used by the login flow).
    safeSessionSet(
      POST_LOGIN_REDIRECT_KEY,
      window.location.pathname + window.location.search
    );
    try {
      await runInteractive(() => instance.acquireTokenRedirect({ scopes, account }));
    } catch (err) {
      // Navigation failed to even start — reset guards so the user can retry.
      redirectInFlight = false;
      safeSessionRemove(REDIRECT_ATTEMPT_KEY);
      // A lock that survived recovery is not something the user can act on, and
      // the raw MSAL text ("Interaction is currently in progress...") is what
      // was being printed into the check-in error banner.
      if (isInteractionInProgressError(err)) {
        throw new Error('Sign-in is already in progress. Reload the page and try again.');
      }
      throw err;
    }
  }

  // Page is navigating away; block callers so they don't proceed with no token.
  return new Promise(() => {});
}

/**
 * Popup fallback. Serializes concurrent popups: only one opens, others wait
 * for it then retry silent (the completed popup refreshes MSAL's cache).
 */
async function acquireViaPopup(instance, account, scopes, returnField) {
  // A full-page redirect already holds MSAL's interaction lock and the page is
  // unloading — opening a popup here would just throw interaction_in_progress.
  if (redirectInFlight) {
    return new Promise(() => {});
  }

  // If a popup is already open, wait for it then retry silent.
  // The completed popup refreshes MSAL's token cache for the account,
  // so silent should succeed for most scope sets afterward.
  if (activePopup) {
    try {
      await activePopup;
    } catch {
      // The other popup may have been dismissed — that's ok, we'll try anyway
    }

    // Retry silent — the cache should be fresher now
    try {
      const response = await instance.acquireTokenSilent({ scopes, account, forceRefresh: true });
      return response[returnField];
    } catch {
      // Still can't get a token silently. If yet another popup started
      // while we were waiting, wait for that one too before opening our own.
      if (activePopup) {
        try { await activePopup; } catch { /* ignore */ }
        try {
          const response = await instance.acquireTokenSilent({ scopes, account, forceRefresh: true });
          return response[returnField];
        } catch {
          // The waited-on popup didn't yield a usable token for these scopes;
          // fall through and open our own popup rather than leaking the error.
        }
      }
      // Fall through to open our own popup
    }
  }

  // Open a popup — we're the first caller that needs interaction
  const popupPromise = runInteractive(() => instance.acquireTokenPopup({ scopes, account }));
  activePopup = popupPromise;

  try {
    const response = await popupPromise;
    return response[returnField];
  } finally {
    // Only clear if we're still the registered interaction
    if (activePopup === popupPromise) {
      activePopup = null;
    }
  }
}
